<script setup lang="ts">
    import InputDecorator, { InputDecoratorProps } from './InputDecorator.vue';
    import { EditorContent, useEditor, BubbleMenu } from '@tiptap/vue-3';
    import Document from '@tiptap/extension-document';
    import Paragraph from '@tiptap/extension-paragraph';
    import Text from '@tiptap/extension-text';
    import Heading from '@tiptap/extension-heading';
    import Bold from '@tiptap/extension-bold';
    import Italic from '@tiptap/extension-italic';
    import Strike from '@tiptap/extension-strike';
    import DropCursor from '@tiptap/extension-dropcursor';
    import GapCursor from '@tiptap/extension-gapcursor';
    import Blockquote from '@tiptap/extension-blockquote';
    import History from '@tiptap/extension-history';
    import HardBreak from '@tiptap/extension-hard-break';
    import HorizontalRule from '@tiptap/extension-horizontal-rule';
    import ListItem from '@tiptap/extension-list-item';
    import BulletList from '@tiptap/extension-bullet-list';
    import OrderedList from '@tiptap/extension-ordered-list';
    import LinkMark from '@tiptap/extension-link';
    import CharacterCount from '@tiptap/extension-character-count';
    import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue';
    import TextInput from './TextInput.vue';
    import Button from '../Buttons/Button.vue';
    import Modal from '../Modal/Modal.vue';
    import { ButtonType } from '../../Composables/button-styles';
    import { useI18n } from 'vue-i18n';
    import Link from '../Buttons/Link.vue';
    import IconButton from '../Buttons/IconButton.vue';
    import type { Editor } from '@tiptap/core';
    import { useMagicKeys } from '@vueuse/core';

    const props = defineProps<{
        id: string;
        value: string;
        cols?: number;
        maxlength?: number;
    } & InputDecoratorProps>();

    const { t } = useI18n();

    const editor = useEditor({
        content: props.value,
        extensions: [
            Document,
            Paragraph,
            Text,
            Heading.configure({
                levels: [1, 2, 3]
            }),
            Bold,
            ListItem,
            BulletList,
            OrderedList,
            Italic,
            Strike,
            DropCursor,
            GapCursor,
            Blockquote,
            History,
            HardBreak,
            HorizontalRule,
            LinkMark.configure({
                linkOnPaste: true,
                openOnClick: true
            }),
            CharacterCount.configure({
                limit: props.maxlength
            })
        ],
        onUpdate: () => emit('update', editor.value?.getHTML() || '')
    });

    watch(() => props.value, (value) => {
        if (editor.value?.getHTML() !== value) {
            editor.value?.commands.setContent(value);
        }
    });

    const { cmd_k, ctrl_k } = useMagicKeys();

    watch([cmd_k, ctrl_k], ([pressedCmd, pressedCtrl]) => {
        if (editor.value && (pressedCmd || pressedCtrl)) {
            toggleLinkMenu();
        }
    });

    onBeforeUnmount(() => {
        editor.value?.destroy();
    });

    const emit = defineEmits<{
        update: [value: string];
    }>();

    const addingLink = ref(false);
    const link = reactive({
        text: '',
        href: ''
    });

    const toggleLinkMenu = () => {
        addingLink.value = !addingLink.value;
        if (addingLink.value && editor.value) {
            if (editor.value.isActive('link')) {
                editor.value.commands.extendMarkRange('link');
            }
            const { selection } = editor.value.view.state;
            link.text = editor.value?.state.doc.textBetween(selection.from, selection.to) || '';
            link.href = editor.value?.getAttributes('link').href || '';
        }
    };

    const copyToClipboard = () => {
        if (editor.value?.getAttributes('link').href && typeof navigator.clipboard !== 'undefined') {
            navigator.clipboard.writeText(editor.value?.getAttributes('link').href);
        }
    };

    const unlink = () => {
        editor.value?.chain().focus().unsetLink().run();
    };

    const addLink = () => {
        if (link) {
            editor.value?.chain()
                .focus()
                .extendMarkRange('link')
                .setLink({ href: link.href })
                .command(({ tr }) => {
                    tr.insertText(link.text);
                    return true;
                })
                .run();
            toggleLinkMenu();
        }
    }

    const shouldDisplayLinkEdition = ({editor, from, to}: {editor: Editor, from: number, to: number}) => {
        if (editor && editor.isActive('link') && !addingLink.value) {
            const $currentNode = editor.$pos(from);
            const nodeStart = $currentNode.range.from;
            const nodeEnd = $currentNode.range.to;

            const selectionStart = from;
            const selectionEnd = to;

            // Check if the selection is within the link node and not on the edges
            return nodeStart < selectionStart && selectionEnd < nodeEnd - 1;
        }
        return false;
    };

    const shouldDisplayCharacterCount = computed(() => {
        if (props.maxlength && editor.value) {
            return editor.value?.storage.characterCount.characters() >= props.maxlength * 0.9;
        }
        return false;
    });
</script>
<template>
    <InputDecorator
        :label="label"
        :errors="errors"
        :help-position="helpPosition"
        :help="help"
        :required="required"
        :for-id="id"
    >
        <Modal class="link-modal" :open="editor && addingLink" @close="toggleLinkMenu">
            <form class="link-modal-container" @submit.prevent="addLink">
                <TextInput
                    id="text"
                    name="text"
                    :label="t('rich_text_editor.link_text')"
                    :value="link.text"
                    @update="value => link.text = value"
                />
                <TextInput
                    id="link"
                    name="link"
                    :label="t('rich_text_editor.link_url')"
                    :value="link.href"
                    @update="value => link.href = value"
                    placeholder="https://example.com"
                />
                <div class="actions-container">
                    <Button @click="toggleLinkMenu" :type="ButtonType.LINK">{{ t('cancel') }}</Button>
                    <Button input-type="submit">{{ t('rich_text_editor.add_link') }}</Button>
                </div>
            </form>
        </Modal>
        <div class="rich-text-input">
            <div class="editor-menu" v-if="editor" :editor="editor">
                <div class="actions">
                    <div :class="['action', 'fa-solid', 'fa-bold', {active: editor.isActive('bold')}]" @click="editor.chain().focus().toggleBold().run()"></div>
                    <div :class="['action', 'fa-solid', 'fa-italic', {active: editor.isActive('italic')}]" @click="editor.chain().focus().toggleItalic().run()"></div>
                    <div :class="['action', 'fa-solid', 'fa-strikethrough', {active: editor.isActive('strike')}]" @click="editor.chain().focus().toggleStrike().run()"></div>
                    <div :class="['action', 'fa-solid', 'fa-link', {active: editor.isActive('link')}]" @click="toggleLinkMenu"></div>
                    <div :class="['action', 'fa-solid', 'fa-quote-left', {active: editor.isActive('blockquote')}]" @click="editor.chain().focus().toggleBlockquote().run()"></div>
                    <div :class="['action', 'fa-solid', 'fa-list-ul', {active: editor.isActive('bulletList')}]" @click="editor.chain().focus().toggleBulletList().run()"></div>
                    <div :class="['action', 'fa-solid', 'fa-list-ol', {active: editor.isActive('orderedList')}]" @click="editor.chain().focus().toggleOrderedList().run()"></div>
                </div>
            </div>
            <BubbleMenu v-if="editor" :should-show="shouldDisplayLinkEdition" :editor="editor">
                <div class="link-edition">
                    <Link
                        class="link-value"
                        target="_blank"
                        :href="editor?.getAttributes('link').href"
                        :title="editor?.getAttributes('link').href"
                    >
                        {{ editor?.getAttributes('link').href }}
                    </Link>
                    <IconButton
                        @click="copyToClipboard"
                        :tooltipText="t('rich_text_editor.copy_to_clipboard')"
                    >
                        <i class="fa-regular fa-clipboard"></i>
                    </IconButton>
                    <IconButton
                        @click="unlink"
                        :tooltipText="t('rich_text_editor.unlink')"
                    >
                        <i class="fa-solid fa-link-slash"></i>
                    </IconButton>
                    <IconButton
                        @click="toggleLinkMenu"
                        :tooltipText="t('rich_text_editor.edit_link')"
                    >
                        <i class="yapla-icon icon-edit"></i>
                    </IconButton>
                </div>
            </BubbleMenu>
            <EditorContent :editor="editor"/>
            <div v-if="shouldDisplayCharacterCount" class="info-bar">
                <p class="info character-count">
                    <span class="count">{{ editor!.storage.characterCount.characters() }}</span> / {{ maxlength }}
                </p>
            </div>
        </div>
    </InputDecorator>
</template>
<style lang="scss">
@import '@Common/Styles/variables';

.link-modal {
    .v-modal-overlay .v-modal .v-modal-content {
        max-width: 460px;
    }

    .link-modal-container {
        display: flex;
        gap: 5px;
        flex-direction: column;

        .actions-container {
            display: flex;
            justify-content: end;
            gap: 5px;
        }
    }
}

.rich-text-input {
    border: 1px solid var(--gray-300, map-get($yapla-gray, 300));
    border-radius: 4px;
    background: white;

    &:focus-within {
        border-color: var(--blue-500, map-get($yapla-blue, 400));
        outline: none;
        box-shadow: 0px 0px 0px 2px var(--blue-200, map-get($yapla-blue, 200));
    }

    .editor-menu {
        width: 100%;
        background: white;
        padding: 5px;
        border-top-left-radius: 4px;
        border-top-right-radius: 4px;
        border-bottom: 1px solid var(--gray-200, map-get($yapla-gray, 200));
        box-shadow: 0px 5px 15px rgba(0, 87, 255, 0.1);

        .actions {
            display: flex;
            gap: 2px;

            .action {
                cursor: pointer;
                padding: 5px;
                border-radius: 4px;
                transition: background-color 0.2s ease, color 0.2s ease;
                color: var(--gray-600, map-get($yapla-gray, 600));

                &:hover {
                    color: var(--gray-700, map-get($yapla-gray, 700));
                    background-color: var(--gray-100, map-get($yapla-gray, 100));
                }

                &.active {
                    color: var(--blue-500, map-get($yapla-blue, 500));
                    background-color: var(--blue-200, map-get($yapla-blue, 100));

                    &:hover {
                        color: var(--blue-500, map-get($yapla-blue, 600));
                        background-color: var(--blue-200, map-get($yapla-blue, 200));
                    }
                }

            }

        }
    }

    .link-edition {
        display: flex;
        gap: 5px;
        align-items: center;
        padding: 4px 8px;
        background: white;
        border-radius: 4px;
        box-shadow: 0px 5px 15px rgba(0, 87, 255, 0.1);
        border: 1px solid var(--gray-200, map-get($yapla-gray, 200));

        .link-value {
            flex: 1;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            max-width: 300px;
        }

        .icon-button {
            padding: 5px;
            border-radius: 4px;
            transition: background-color 0.2s ease;

            &:hover {
                background-color: var(--gray-100, map-get($yapla-gray, 100));
            }
        }
    }

    .info-bar {
        display: flex;
        align-items: center;
        justify-content: end;
        gap: 5px;
        padding: 5px;
        border-top: 1px solid var(--gray-200, map-get($yapla-gray, 200));
        color: var(--gray-500, map-get($yapla-gray, 500));

        .info {
            margin: 0;
        }

        .character-count .count {
            color: var(--gray-600, map-get($yapla-gray, 600));
        }
    }

    .tiptap {
        padding: 10px;
        background: white;
        min-height: var(--rich-text-editor-min-height, 250px);
        border-bottom-left-radius: 4px;
        border-bottom-right-radius: 4px;

        &:focus {
            outline: none;
        }

        blockquote {
            border-left: 2px solid var(--gray-300, map-get($yapla-gray, 300));
            padding-left: 10px;
            margin-left: 0;
        }

        a {
            pointer-events: none;
            cursor: text;
        }
    }

}

</style>